import {
  Checkbox,
  ColumnsIcon,
  Divider,
  FormControlLabel,
  FormGroup,
  Popover,
  PopoverProps,
  Theme,
  Typography,
  makeStyles,
} from '@forgeglobal/design-framework'
import { Column, ColumnApi, GridApi } from 'ag-grid-community'
import { StyleDefaults } from 'isolated-design-library/ThemeProvider'
import React, { useRef, useState } from 'react'
import scssStyle from './ColumnsChooserMenu.module.scss'

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    boxShadow: '0px 2px 16px 0px rgba(153, 155, 168, 0.2)',
    minWidth: 220,
    transform: 'scale(1, 1) translateZ(0px) translateY(2px) !important',
    '& > *': {
      paddingTop: 0,
    },
  },
  header: {
    padding: theme.spacing(3, 2, 2.5),
    display: 'flex',
    alignItems: 'baseline',
    color: theme.palette.secondary.main,
  },
  headerIcon: {
    fontSize: 20,
    paddingRight: theme.spacing(2),
  },
  search: {
    borderTop: StyleDefaults.border,
    borderBottom: StyleDefaults.border,
    background: StyleDefaults.secondaryBackgroundColor,
  },
  formGroup: {
    maxHeight: 400,
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    overflowY: 'auto',
  },
  formItem: {
    padding: theme.spacing(0.5, 2),
  },
  formLabel: {
    marginLeft: 0,
  },
  label: {
    color: StyleDefaults.textDark,
    fontSize: 13,
    fontWeight: 400,
  },
  checkbox: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 1, 0.5, -1.25),
  },
  noResults: {
    height: theme.spacing(7),
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

export const ColumnsChooserMenu = ({
  gridApi,
  columnApi,
  ...rest
}: PopoverProps & { gridApi: GridApi; columnApi: ColumnApi }) => {
  const styles = useStyles()
  const [counter, setCounter] = useState(0)
  const rerender = () => setCounter(counter + 1)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const [searchValue, setSearchValue] = useState('')

  // all columns
  const visibleColumnIds: { [key: string]: boolean } = Object.assign(
    {},
    ...columnApi.getAllDisplayedColumns().map(c => ({ [c.getUniqueId()]: true }))
  )

  // toggle
  const setColumnVisible = (column: Column, isVisible: boolean) => {
    columnApi.setColumnVisible(column, isVisible)
    rerender()
  }

  // filter by search query
  const columns = columnApi.getAllColumns()
  const baseFilteredColumns = columns.filter(c => {
    const name = columnApi.getDisplayNameForColumn(c, null).toLowerCase()
    return name !== 'detail' && name !== 'name'
  })
  const filteredColumns = baseFilteredColumns.filter(c =>
    columnApi
      .getDisplayNameForColumn(c, null)
      .toLowerCase()
      .includes(searchValue.toLowerCase())
  )

  // focus on search after mount, as autofocus doesn't work
  setTimeout(() => searchRef.current && searchRef.current.focus(), 0)

  const openColumnCount = Object.keys(visibleColumnIds).length
  const allColumnSelected = openColumnCount === columns.length
  const noColumnsSelected = openColumnCount === columns.length - baseFilteredColumns.length

  const onSelectAllClicked = () => {
    const allColumnIds = baseFilteredColumns.map(col => col.getUniqueId())
    columnApi.setColumnsVisible(allColumnIds, !allColumnSelected)
    rerender()
  }

  return (
    <Popover
      classes={{ paper: styles.wrapper }}
      keepMounted
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      transitionDuration={0}
      {...rest}>
      <div className={styles.header}>
        <ColumnsIcon className={styles.headerIcon} />
        <Typography variant="h6" color="secondary">
          Choose Columns
        </Typography>
      </div>
      <div className={styles.search}>
        <input
          placeholder="Filter..."
          ref={searchRef}
          autoFocus
          onChange={e => setSearchValue(e.target.value)}
          value={searchValue}
          className={scssStyle.input}
        />
      </div>

      <FormControlLabel
        classes={{ root: styles.formLabel, label: styles.label }}
        className={styles.formItem}
        control={
          <Checkbox
            classes={{ root: styles.checkbox }}
            checked={!noColumnsSelected}
            indeterminate={!allColumnSelected && !noColumnsSelected}
            onChange={onSelectAllClicked}
          />
        }
        label="(Select All)"
      />
      <Divider />
      <FormGroup className={styles.formGroup}>
        {filteredColumns.map(column => (
          <FormControlLabel
            key={column.getUniqueId()}
            classes={{ root: styles.formLabel, label: styles.label }}
            className={styles.formItem}
            control={
              <Checkbox
                classes={{ root: styles.checkbox }}
                checked={!!visibleColumnIds[column.getUniqueId()]}
                onChange={() => setColumnVisible(column, !visibleColumnIds[column.getUniqueId()])}
              />
            }
            label={columnApi.getDisplayNameForColumn(column, null)}
          />
        ))}
        {!filteredColumns.length && (
          <Typography color="textSecondary" variant="body2" className={styles.noResults}>
            No columns with that name
          </Typography>
        )}
      </FormGroup>
    </Popover>
  )
}
